import { Navigate, Route, Routes } from 'react-router-dom'
import {
  DashboardUrl,
  MemberInviteUrl,
  PayPeriodDetailUrl,
  PayPeriodsUrl,
  TeamComplianceDetailUrl,
  TeamComplianceUrl,
  TeamDataExportUrl,
  TeamFboWalletUrl,
  TeamMembersUrl,
  TeamPremiumUrl,
  TeamReportsUrl,
  TeamSummaryUrl,
  TeamsUrl,
  TeamWalletUrl
} from 'constants/RouteUrls'

import React, { lazy, Suspense } from 'react'
import { useSettings } from 'core/hooks/useSettings'
import useRolesAndPermissions from 'core/hooks/useRolesAndPermissions'
import { useTeam, useUserProfile } from 'core/hooks/useDataHook'
import { ID } from 'types/entity'
import CloudEvent from 'utils/CloudEvent'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import NotFoundPage from 'routes/NotFoundPage'
import LazyLoadingSkeleton from 'core/components/loaders/LazyLoadingSkeleton'
import { lazyLoadHelper } from 'routes/resources'
import useSubscription from 'core/hooks/useSubscription'

const DataExportPage = lazy(() =>
  lazyLoadHelper(import('pages/shared/data-export/DataExportPage'))
)
const ManagerReportsPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/manager-reports/ManagerReportsPage'))
)
const SummaryPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/summary/SummaryPage'))
)
const PremiumPage = lazy(() =>
  lazyLoadHelper(import('pages/shared/premium/PremiumPage'))
)
const TeamHomePage = lazy(() =>
  lazyLoadHelper(import('pages/teams/home/TeamsHomePage'))
)
const Compliance = lazy(() => lazyLoadHelper(import('pages/teams/compliance')))
const MembersPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/members/MembersPage'))
)
const TeamsPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/teams/TeamsPage'))
)
const ReimbursementPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/reimbursements/ReimbursementsPage'))
)
const WalletPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/wallet/WalletPage'))
)
const FboWalletPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/wallet/fbo/FboWalletPage'))
)
const InvitationPage = lazy(() =>
  lazyLoadHelper(import('pages/teams/member-invite/MemberInvitePage'))
)
const ReviewerFormPage = lazy(() =>
  lazyLoadHelper(
    import('pages/teams/compliance/compliance-detail-page/ReviewerFormPage')
  )
)
const PayPeriodDetailPage = lazy(() =>
  lazyLoadHelper(
    import('pages/teams/reimbursements/pay-period-detail/PayPeriodDetailPage')
  )
)

const cleanWildcard = (string: string) => {
  return string.replace('/teams/', '')
}

const TeamsDashboardRoutes = () => {
  const { isPremium } = useSubscription()
  const { data: userProfile } = useUserProfile()
  const { token_id } = userProfile?.teams?.[0] ?? {}
  const {
    data: {
      featureFlags: { ENABLE_EVERLANCE_PAYMENTS }
    }
  } = useSettings()

  useTeam({
    params: {
      teamId: token_id as ID
    },
    opts: {
      enabled: !!token_id,
      onSuccess: (data) => {
        const properties = {} as Record<string, boolean>
        properties[CloudEvent.TagsEnabled] = data.tags_enabled
        properties[CloudEvent.CategoriesEnabled] = data.categories_enabled
        AnalyticsHelper.addPeopleProperty(
          userProfile?.token_id ?? '',
          properties
        )
      }
    }
  })

  const {
    permissions: { hasAccessToTeamDashboard, hasFullPower }
  } = useRolesAndPermissions()

  if (!hasAccessToTeamDashboard) {
    return <Navigate to={DashboardUrl} replace />
  }

  return (
    <Routes>
      <Route
        path={'/'}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="team-home" />}>
            <TeamHomePage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamDataExportUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
            <DataExportPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamReportsUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
            <ManagerReportsPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamSummaryUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
            <SummaryPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamPremiumUrl)}
        element={
          <Suspense
            fallback={
              <LazyLoadingSkeleton
                variant={isPremium ? 'billing' : 'premium'}
              />
            }
          >
            <PremiumPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamComplianceUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" withBanner />}
          >
            <Compliance />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamMembersUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
            <MembersPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamsUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
            <TeamsPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamComplianceDetailUrl)}
        element={
          <Suspense
            fallback={
              <LazyLoadingSkeleton
                variant="default"
                isChildPage
                withBreadcrumbs
              />
            }
          >
            <ReviewerFormPage />
          </Suspense>
        }
      />

      {/*  Schedules routes*/}
      <Route
        path={cleanWildcard(PayPeriodsUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
            <ReimbursementPage />
          </Suspense>
        }
      />

      {/*  Wallet*/}
      {ENABLE_EVERLANCE_PAYMENTS && hasFullPower ? (
        <>
          <Route
            path={cleanWildcard(TeamWalletUrl)}
            element={
              <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
                <WalletPage />
              </Suspense>
            }
          />
          <Route
            path={cleanWildcard(TeamFboWalletUrl)}
            element={
              <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
                <FboWalletPage />
              </Suspense>
            }
          />
        </>
      ) : (
        <></>
      )}

      {/* Excluded from menu layout*/}
      <Route
        path={cleanWildcard(MemberInviteUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" isChildPage />}
          >
            <InvitationPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(PayPeriodDetailUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="pay-period" isChildPage />}
          >
            <PayPeriodDetailPage />
          </Suspense>
        }
      />

      <Route path={'*'} element={<NotFoundPage />} />
    </Routes>
  )
}

export default TeamsDashboardRoutes
